import { layersApi } from '@/api/layers';
import { layerDefaultWellProfilesApi } from '@/api/layerDefaultWellProfiles';
import { scenarioLayersApi } from '@/api/scenarioLayers';
import { FieldMarkerCluster } from '@/components/Map/FieldMarkerCluster';
import { createScenarioLocationMapIcon } from '@/components/Map/fieldMapIcon';
import { MapBaseLayers } from '@/components/Map/mapLayers';
import {
  FIELD_MARKER_ZOOM,
  MENA_DEFAULT_ZOOM,
  MENA_MAP_CENTER,
} from '@/constants/fieldMap';
import { isHiddenLayerAttribute } from '@/data/hiddenLayerAttributes';
import { formatAttributeLabel } from '@/utils/attributeLabel';
import type { LatLng } from '@/utils/fieldCoordinate';
import { parsePointCoordinate } from '@/utils/fieldCoordinate';
import {
  buildDefaultProfilesFromCoefs,
  type DefaultProfilePoint,
} from '@/utils/layerProductionCharts';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Search as SearchIcon } from '@mui/icons-material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, useMap, useMapEvents, ZoomControl } from 'react-leaflet';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const PROPERTY_DECIMAL_PLACES = 3;
const FORM_NAV_WIDTH = 280;
const FORM_PANEL_WIDTH = 600;
const MAP_EDGE_PADDING = 16;
const TYPE_WELL_CHART_HEIGHT = 200;
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 25;
const OIL_COLOR = '#5D4037';
const LIQUID_COLOR = '#1565C0';
const WC_COLOR = '#C62828';
const SELECTED_PIN_COLOR = '#d32f2f';

interface AnalogueMapField {
  id: string;
  name: string;
  coordinate: string | null;
}

interface AnalogueLayerRow {
  layer_id: string;
  layer_name: string;
  type: string | null;
  has_type_well_curve: boolean;
}

interface LayerPropertyRow {
  id: string;
  attribute_id?: string;
  name: string;
  unit?: string | null;
  value: string | number | null;
  description?: string | null;
}

interface AnalogueMapSearchDialogProps {
  open: boolean;
  selectedLayerIds: string[];
  adding?: boolean;
  /** Optional GeoJSON Point text for the scenario location. */
  scenarioCoordinate?: string | null;
  onClose: () => void;
  onAdd: (analogueLayerId: string) => void;
}

type MapFieldPoint = {
  id: string;
  position: LatLng;
  data: AnalogueMapField;
};

function formatPropertyValue(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toFixed(PROPERTY_DECIMAL_PLACES);
  }
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return String(value);
}

function MapFitBounds({
  positions,
  scenarioPosition,
}: {
  positions: LatLng[];
  scenarioPosition: LatLng | null;
}) {
  const map = useMap();

  useEffect(() => {
    // Prefer scenario location when opening the map.
    if (scenarioPosition) {
      map.setView(
        [scenarioPosition.lat, scenarioPosition.lng],
        Math.max(FIELD_MARKER_ZOOM, 10),
        { animate: false },
      );
      return;
    }
    if (positions.length === 0) {
      return;
    }
    if (positions.length === 1) {
      map.setView([positions[0].lat, positions[0].lng], FIELD_MARKER_ZOOM, { animate: false });
      return;
    }
    const bounds = L.latLngBounds(positions.map((p) => [p.lat, p.lng] as [number, number]));
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 10 });
  }, [map, positions, scenarioPosition]);

  return null;
}

function ScenarioLocationMarker({ position }: { position: LatLng }) {
  const map = useMap();

  useEffect(() => {
    const marker = L.marker([position.lat, position.lng], {
      icon: createScenarioLocationMapIcon('Scenario'),
      zIndexOffset: 20000,
      interactive: false,
    });
    marker.addTo(map);
    return () => {
      map.removeLayer(marker);
    };
  }, [map, position.lat, position.lng]);

  return null;
}

/** Pan/zoom to a pin when a mapped field is selected from the list or map. */
function MapFocusPosition({ position }: { position: LatLng | null }) {
  const map = useMap();

  useEffect(() => {
    if (!position) return;
    const targetZoom = Math.max(map.getZoom(), FIELD_MARKER_ZOOM, 10);
    map.flyTo([position.lat, position.lng], targetZoom, { animate: true, duration: 0.6 });
  }, [map, position]);

  return null;
}

function MapBackgroundClickHandler({ onBackgroundClick }: { onBackgroundClick: () => void }) {
  useMapEvents({
    click: () => {
      onBackgroundClick();
    },
  });
  return null;
}

function fieldHasMapPosition(field: AnalogueMapField | null | undefined): boolean {
  if (!field?.coordinate) return false;
  return Boolean(parsePointCoordinate(field.coordinate));
}

export const AnalogueMapSearchDialog: React.FC<AnalogueMapSearchDialogProps> = ({
  open,
  selectedLayerIds,
  adding = false,
  scenarioCoordinate = null,
  onClose,
  onAdd,
}) => {
  const [mapReady, setMapReady] = useState(false);
  const [mapFieldsLoading, setMapFieldsLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapFields, setMapFields] = useState<AnalogueMapField[]>([]);
  const [listFields, setListFields] = useState<AnalogueMapField[]>([]);
  const [listTotal, setListTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [selectedField, setSelectedField] = useState<AnalogueMapField | null>(null);
  const [layers, setLayers] = useState<AnalogueLayerRow[]>([]);
  const [layersLoading, setLayersLoading] = useState(false);

  const [selectedLayer, setSelectedLayer] = useState<AnalogueLayerRow | null>(null);
  const [properties, setProperties] = useState<LayerPropertyRow[]>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [typeWellCurves, setTypeWellCurves] = useState<DefaultProfilePoint[]>([]);
  const [curvesLoading, setCurvesLoading] = useState(false);
  const [curvesError, setCurvesError] = useState<string | null>(null);
  const [noCurvesOpen, setNoCurvesOpen] = useState(false);
  const [panTarget, setPanTarget] = useState<LatLng | null>(null);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => setMapReady(true), 50);
    return () => {
      window.clearTimeout(timer);
      setMapReady(false);
    };
  }, [open]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const loadMapFields = useCallback(async () => {
    setMapFieldsLoading(true);
    try {
      const response = await scenarioLayersApi.listAnalogueMapFields();
      setMapFields(response.fields || []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load fields for map');
      setMapFields([]);
    } finally {
      setMapFieldsLoading(false);
    }
  }, []);

  const loadListFields = useCallback(async () => {
    setListLoading(true);
    setError(null);
    try {
      const response = await scenarioLayersApi.listAnalogueFields(
        debouncedSearch,
        (page - 1) * PAGE_SIZE,
        PAGE_SIZE,
      );
      setListFields(
        (response.fields || []).map((field) => ({
          id: field.id,
          name: field.name,
          coordinate: field.coordinate ?? null,
        })),
      );
      setListTotal(response.total || 0);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load deposits');
      setListFields([]);
      setListTotal(0);
    } finally {
      setListLoading(false);
    }
  }, [debouncedSearch, page]);

  useEffect(() => {
    if (!open) return;
    void loadMapFields();
  }, [open, loadMapFields]);

  useEffect(() => {
    if (!open) return;
    void loadListFields();
  }, [open, loadListFields]);

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setDebouncedSearch('');
      setPage(1);
      setSelectedField(null);
      setLayers([]);
      setSelectedLayer(null);
      setProperties([]);
      setTypeWellCurves([]);
      setCurvesError(null);
      setError(null);
      setNoCurvesOpen(false);
      setPanTarget(null);
      setListFields([]);
      setListTotal(0);
    }
  }, [open]);

  const mapPoints = useMemo<MapFieldPoint[]>(() => {
    return mapFields.flatMap((field) => {
      const position = parsePointCoordinate(field.coordinate);
      if (!position) return [];
      return [{ id: field.id, position, data: field }];
    });
  }, [mapFields]);

  const scenarioPosition = useMemo(
    () => parsePointCoordinate(scenarioCoordinate),
    [scenarioCoordinate],
  );

  const mapCenter = useMemo<[number, number]>(() => {
    if (scenarioPosition) {
      return [scenarioPosition.lat, scenarioPosition.lng];
    }
    if (mapPoints.length > 0) {
      return [mapPoints[0].position.lat, mapPoints[0].position.lng];
    }
    return [MENA_MAP_CENTER.lat, MENA_MAP_CENTER.lng];
  }, [mapPoints, scenarioPosition]);

  const initialZoom = mapPoints.length > 0 ? FIELD_MARKER_ZOOM : MENA_DEFAULT_ZOOM;
  const positions = useMemo(() => mapPoints.map((p) => p.position), [mapPoints]);
  const pageCount = Math.max(1, Math.ceil(listTotal / PAGE_SIZE));

  /** Pin highlight only when the selected field has coordinates. */
  const selectedMapId = fieldHasMapPosition(selectedField) ? selectedField!.id : null;

  const selectField = useCallback(async (field: AnalogueMapField, opts?: { focusPin?: boolean }) => {
    setSelectedField(field);
    setSelectedLayer(null);
    setProperties([]);
    setTypeWellCurves([]);
    setCurvesError(null);
    setLayersLoading(true);
    setError(null);

    const position = parsePointCoordinate(field.coordinate);
    if (position && opts?.focusPin) {
      setPanTarget(position);
    } else {
      setPanTarget(null);
    }

    try {
      const payload = await scenarioLayersApi.listAnalogueFieldLayers(field.id);
      setLayers(payload.layers || []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load reservoirs');
      setLayers([]);
    } finally {
      setLayersLoading(false);
    }
  }, []);

  const handleSelectPoint = useCallback((point: MapFieldPoint) => {
    void selectField(point.data, { focusPin: true });
  }, [selectField]);

  const handleSelectFieldFromList = useCallback((field: AnalogueMapField) => {
    // With coordinates: activate pin and zoom to it.
    // Without: clear pin highlight (selectedMapId null) and keep current view.
    void selectField(field, { focusPin: fieldHasMapPosition(field) });
  }, [selectField]);

  const handleClearFieldSelection = useCallback(() => {
    setSelectedField(null);
    setLayers([]);
    setSelectedLayer(null);
    setProperties([]);
    setTypeWellCurves([]);
    setCurvesError(null);
    setPanTarget(null);
  }, []);

  const openLayer = async (layer: AnalogueLayerRow) => {
    setSelectedLayer(layer);
    setPropertiesLoading(true);
    setCurvesLoading(true);
    setProperties([]);
    setTypeWellCurves([]);
    setCurvesError(null);
    setError(null);
    try {
      const [details, coefs] = await Promise.all([
        layersApi.getLayer(layer.layer_id),
        layerDefaultWellProfilesApi.getCoefsByLayer(layer.layer_id).catch(() => null),
      ]);
      setProperties((details.properties || []) as LayerPropertyRow[]);
      if (coefs) {
        setTypeWellCurves(buildDefaultProfilesFromCoefs(coefs));
      } else {
        setTypeWellCurves([]);
        setCurvesError('Type-well curves are not available for this reservoir.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load reservoir properties');
      setProperties([]);
      setTypeWellCurves([]);
      setCurvesError(null);
    } finally {
      setPropertiesLoading(false);
      setCurvesLoading(false);
    }
  };

  const handleAdd = () => {
    if (!selectedLayer) return;
    if (selectedLayerIds.includes(selectedLayer.layer_id)) return;
    if (selectedLayerIds.length >= 10) {
      setError('At most 10 analogues can be selected');
      return;
    }
    if (!selectedLayer.has_type_well_curve) {
      setNoCurvesOpen(true);
      return;
    }
    onAdd(selectedLayer.layer_id);
  };

  const getLabel = useCallback((point: MapFieldPoint) => point.data.name, []);
  const visibleProperties = properties.filter(
    (prop) => !isHiddenLayerAttribute(prop.attribute_id || prop.id, prop.name),
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
        <DialogTitle>Add analogue by name</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {error && (
            <Alert severity="error" sx={{ m: 2, mb: 0 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Box
            sx={{
              display: 'flex',
              height: 560,
              minHeight: 0,
              '& .field-map-labeled-marker': {
                background: 'transparent',
                border: 'none',
                // Leaflet sets width/height from iconSize; keep overflow visible for paint,
                // but the element box itself must cover pin + label for hit-testing.
              },
              '& .field-map-labeled-marker__wrap': {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: 140,
                height: 79,
                pointerEvents: 'auto',
                cursor: 'pointer',
              },
              '& .field-map-labeled-marker__pin': {
                width: 25,
                height: 41,
                display: 'block',
                flexShrink: 0,
                pointerEvents: 'auto',
              },
              '& .field-map-labeled-marker__label': {
                marginTop: '2px',
                maxWidth: 136,
                maxHeight: 36,
                overflow: 'hidden',
                padding: '2px 6px',
                borderRadius: 4,
                backgroundColor: 'rgba(255,255,255,0.92)',
                border: '1px solid rgba(0,0,0,0.18)',
                color: '#1a1a1a',
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1.2,
                textAlign: 'center',
                wordBreak: 'break-word',
                boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                pointerEvents: 'auto',
                userSelect: 'none',
              },
              '& .field-map-labeled-marker--selected .field-map-labeled-marker__label': {
                backgroundColor: SELECTED_PIN_COLOR,
                color: '#fff',
                borderColor: '#8e0000',
              },
              '& .field-map-labeled-marker--scenario .field-map-labeled-marker__label': {
                backgroundColor: '#d32f2f',
                color: '#fff',
                borderColor: '#8e0000',
              },
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                position: 'relative',
                pl: `${MAP_EDGE_PADDING}px`,
                py: `${MAP_EDGE_PADDING}px`,
                pr: 0,
              }}
            >
              {mapFieldsLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12 + MAP_EDGE_PADDING,
                    left: 12 + MAP_EDGE_PADDING,
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                  }}
                >
                  <CircularProgress size={18} />
                  <Typography variant="caption">Loading fields...</Typography>
                </Box>
              )}
              {!mapFieldsLoading && mapPoints.length === 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12 + MAP_EDGE_PADDING,
                    left: 12 + MAP_EDGE_PADDING,
                    right: 12,
                    zIndex: 1000,
                  }}
                >
                  <Alert severity="info">
                    No deposits with map coordinates. Use the list to select a field by name.
                  </Alert>
                </Box>
              )}
              <Box
                sx={{
                  height: '100%',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: 1,
                  borderColor: 'divider',
                  '& .leaflet-container': { height: '100%', width: '100%', zIndex: 0 },
                  '& .leaflet-control-layers': { fontSize: '12px' },
                }}
              >
                {mapReady ? (
                  <MapContainer
                    center={mapCenter}
                    zoom={initialZoom}
                    minZoom={2}
                    maxZoom={14}
                    scrollWheelZoom
                    style={{ height: '100%', width: '100%' }}
                    zoomControl={false}
                  >
                    <MapBaseLayers />
                    <ZoomControl position="bottomright" />
                    <MapFitBounds positions={positions} scenarioPosition={scenarioPosition} />
                    <MapFocusPosition position={panTarget} />
                    {scenarioPosition && <ScenarioLocationMarker position={scenarioPosition} />}
                    <MapBackgroundClickHandler onBackgroundClick={handleClearFieldSelection} />
                    <FieldMarkerCluster
                      points={mapPoints}
                      onSelect={handleSelectPoint}
                      showLabels
                      getLabel={getLabel}
                      selectedId={selectedMapId}
                      disableClustering
                    />
                  </MapContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Loading map...
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                width: FORM_PANEL_WIDTH,
                flexShrink: 0,
                borderLeft: 1,
                borderColor: 'divider',
                display: 'flex',
                minHeight: 0,
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  width: FORM_NAV_WIDTH,
                  flexShrink: 0,
                  borderRight: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
              >
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  {selectedField ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <IconButton
                        size="small"
                        aria-label="Back to deposits"
                        onClick={handleClearFieldSelection}
                      >
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="subtitle2" noWrap title={selectedField.name}>
                        {selectedField.name}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Deposits
                    </Typography>
                  )}
                  {!selectedField && (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search deposits..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  {!selectedField ? (
                    listLoading ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Loading deposits...
                        </Typography>
                      </Box>
                    ) : listFields.length ? (
                      <List sx={{ py: 1 }}>
                        {listFields.map((field) => {
                          const onMap = fieldHasMapPosition(field);
                          return (
                            <ListItem key={field.id} disablePadding>
                              <ListItemButton
                                onClick={() => handleSelectFieldFromList(field)}
                                sx={{ mx: 1, borderRadius: 1, mb: 0.5 }}
                              >
                                <ListItemText
                                  primary={field.name}
                                  secondary={onMap ? undefined : 'No map location'}
                                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    ) : (
                      <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {debouncedSearch
                            ? 'No deposits match this name.'
                            : 'No deposits available.'}
                        </Typography>
                      </Box>
                    )
                  ) : layersLoading ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : layers.length ? (
                    <List sx={{ py: 1 }}>
                      {layers.map((layer) => (
                        <ListItem key={layer.layer_id} disablePadding>
                          <ListItemButton
                            selected={selectedLayer?.layer_id === layer.layer_id}
                            onClick={() => openLayer(layer)}
                            sx={{
                              mx: 1,
                              borderRadius: 1,
                              mb: 0.5,
                              '&.Mui-selected': {
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                                '&:hover': { backgroundColor: 'primary.dark' },
                              },
                            }}
                          >
                            <ListItemText
                              primary={layer.layer_name}
                              secondary={layer.type || undefined}
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: selectedLayer?.layer_id === layer.layer_id ? 600 : 400,
                              }}
                              secondaryTypographyProps={{
                                fontSize: '0.75rem',
                                color: selectedLayer?.layer_id === layer.layer_id
                                  ? 'inherit'
                                  : 'text.secondary',
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No reservoirs in this field.
                      </Typography>
                    </Box>
                  )}
                </Box>
                {!selectedField && listTotal > PAGE_SIZE && (
                  <Box sx={{ px: 1, py: 1, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
                    <Pagination
                      count={pageCount}
                      page={Math.min(page, pageCount)}
                      onChange={(_, value) => setPage(value)}
                      size="small"
                      color="primary"
                      siblingCount={0}
                      boundaryCount={1}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {listTotal} deposit{listTotal === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
              >
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2">Type-well & properties</Typography>
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
                  {!selectedLayer ? (
                    <Typography variant="body2" color="text.secondary">
                      {selectedField
                        ? 'Select a reservoir to view its type-well curves and properties.'
                        : 'Select a deposit from the list or on the map.'}
                    </Typography>
                  ) : propertiesLoading || curvesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {selectedLayer.layer_name}
                      </Typography>

                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Type-well curves
                      </Typography>
                      {typeWellCurves.length > 0 ? (
                        <Box sx={{ width: '100%', height: TYPE_WELL_CHART_HEIGHT, mb: 2 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                              data={typeWellCurves}
                              margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                              <XAxis
                                dataKey="year"
                                tick={{ fontSize: 11 }}
                                label={{ value: 'Year', position: 'insideBottom', offset: -2, fontSize: 11 }}
                              />
                              <YAxis
                                yAxisId="rates"
                                tick={{ fontSize: 11 }}
                                width={42}
                              />
                              <YAxis
                                yAxisId="wc"
                                orientation="right"
                                domain={[0, 100]}
                                tick={{ fontSize: 11 }}
                                width={36}
                                tickFormatter={(v) => `${v}`}
                              />
                              <Tooltip
                                formatter={(value, name) => {
                                  const numeric = Number(value);
                                  if (name === 'Water cut') {
                                    return [`${numeric.toFixed(2)}%`, name];
                                  }
                                  return [numeric.toFixed(3), String(name)];
                                }}
                              />
                              <Legend wrapperStyle={{ fontSize: 12 }} />
                              <Line
                                yAxisId="rates"
                                type="monotone"
                                dataKey="oil"
                                stroke={OIL_COLOR}
                                name="Oil"
                                strokeWidth={2}
                                dot={false}
                                connectNulls
                              />
                              <Line
                                yAxisId="rates"
                                type="monotone"
                                dataKey="liquid"
                                stroke={LIQUID_COLOR}
                                name="Liquid"
                                strokeWidth={2}
                                strokeDasharray="4 4"
                                dot={false}
                                connectNulls
                              />
                              <Line
                                yAxisId="wc"
                                type="monotone"
                                dataKey="waterCut"
                                stroke={WC_COLOR}
                                name="Water cut"
                                strokeWidth={2}
                                dot={false}
                                connectNulls
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </Box>
                      ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          {curvesError || 'No type-well curves available for this reservoir.'}
                        </Alert>
                      )}

                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Properties
                      </Typography>
                      {visibleProperties.length ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Property</TableCell>
                              <TableCell>Value</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {visibleProperties.map((prop) => (
                              <TableRow key={prop.id || prop.attribute_id}>
                                <TableCell>{formatAttributeLabel(prop.name, prop.unit)}</TableCell>
                                <TableCell>{formatPropertyValue(prop.value)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Alert severity="info">No properties available for this reservoir.</Alert>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!selectedLayer || adding}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={noCurvesOpen} onClose={() => setNoCurvesOpen(false)}>
        <DialogTitle>Cannot add analogue</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This reservoir cannot be added because it has no oil and water-cut type-well curves.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoCurvesOpen(false)} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
